// Gemerkte Listenansichten je Nutzer und Spalten der Lead-Listen (Runde 5, Agent 5B): Typen und Client nach crm/API.md, Abschnitt 20.2.
// Die Schlüssel der Listen baut `ListenSchluessel` in components/smartview/listenVertrag.ts.
import { http } from "./client";
import type { LeadListe } from "./types";

export interface ListenSortierungGemerkt {
  key: string;
  ab: boolean;
}

export interface GemerkteAnsicht {
  liste: string;
  /** Schlüssel der gezeigten Spalten in Reihenfolge, null = Standard der Liste */
  spalten: string[] | null;
  sortierung: ListenSortierungGemerkt[] | null;
  /** alles Weitere (Breiten, "nur aktive" …); oberste Schlüssel werden beim Merken eingemischt */
  einstellungen: Record<string, unknown>;
  zuletzt_genutzt_at: string | null;
  nutzungen: number;
  date_updated: string | null;
}

/** Nur die gesendeten Teile ändern sich; `null` setzt den Teil auf den Standard zurück. */
export interface AnsichtEingabe {
  spalten?: string[] | null;
  sortierung?: ListenSortierungGemerkt[] | null;
  einstellungen?: Record<string, unknown> | null;
}

/** Eine wählbare Spalte der Lead-Listen (Antwort von `GET /api/listen-ansichten/felder/leads`). */
export interface KatalogFeld {
  key: string;
  label: string;
  gruppe: string;
  /** text | zahl | zeit | datum | wahl | nutzer | status | enum | dauer | symbole */
  typ: string;
  sortierbar: boolean;
  fest: boolean;
  /** Wert steht nicht in der Lead-Zeile, sondern kommt aus `werteApi.leads` */
  berechnet: boolean;
}

export interface FelderKatalog {
  art: "leads";
  felder: KatalogFeld[];
  standard: string[];
  aliase: Record<string, string>;
  sortierung_standard: ListenSortierungGemerkt[];
}

export interface SmartViewZeiten {
  id: string;
  date_updated: string | null;
  zuletzt_genutzt_at: string | null;
  nutzungen: number;
}

const kodiert = encodeURIComponent;

export const listenAnsichtenApi = {
  alle: () => http.get<{ data: GemerkteAnsicht[] }>("/api/listen-ansichten"),
  eine: (liste: string) => http.get<{ ansicht: GemerkteAnsicht | null }>(`/api/listen-ansichten/${kodiert(liste)}`),
  merken: (liste: string, eingabe: AnsichtEingabe) => http.put<{ ansicht: GemerkteAnsicht }>(`/api/listen-ansichten/${kodiert(liste)}`, eingabe),
  zuruecksetzen: (liste: string) => http.del<void>(`/api/listen-ansichten/${kodiert(liste)}`),
  /** Liste als eben geöffnet merken ("zuletzt genutzt") */
  genutzt: (liste: string) => http.post<void>(`/api/listen-ansichten/${kodiert(liste)}/genutzt`),
  /** Katalog der Spalten und Sortierfelder der Lead-Listen */
  felderLeads: () => http.get<FelderKatalog>("/api/listen-ansichten/felder/leads"),
  /** berechnete Spaltenwerte: { lead_id: { schluessel: wert } } (höchstens 1.000 Leads und 30 Felder je Aufruf) */
  werteLeads: (lead_ids: string[], felder: string[]) =>
    http.post<{ data: Record<string, Record<string, unknown>> }>("/api/listen-ansichten/werte/leads", { lead_ids, felder }),
  /** lokale Lead-Liste nach bis zu drei Feldern des Katalogs sortiert, z. B. sort = "-num_calls,display_name" */
  zeilenLeads: (p: { sort?: string; status_id?: string[]; q?: string; limit?: number; cursor?: string | null }) =>
    http.get<LeadListe>("/api/listen-ansichten/zeilen/leads", {
      sort: p.sort,
      status_id: p.status_id?.length ? p.status_id.join(",") : undefined,
      q: p.q,
      limit: p.limit,
      cursor: p.cursor ?? undefined,
    }),
  /** Zeiten für die Smart-View-Übersicht: zuletzt geändert, zuletzt von mir genutzt */
  smartViewZeiten: () => http.get<{ data: SmartViewZeiten[] }>("/api/listen-ansichten/uebersicht/smartviews"),
};
